import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

import {
    DEFAULT_CLIDS,
    NATIVE_APP_CLIDS,
    PWA_APP_CLIDS,
    TEST_GAMES,
    UNKNOWN_CLID,
    WHITE_LABEL_CLIDS,
} from '@/constants/general'
import type { SelectedExperimentsData } from '@/modules/experiments/types'
import { ROUTE_NAMES } from '@/router/constants'
import { isDebugQueryEnabled, SSR_QUERY } from '@/router/query-params'
import { isBestWebGamesHost } from '@/router/white-label/utils'
import type { ClientType, PlatformType, RankingParams } from '@/types'
import { compareVersionStrings } from '@/utils/device'

export type ErrorStatus = 404 | 410 | 500
export type PageStatus = 200 | 301 | 302 | ErrorStatus
export type AppBootstrapContext = {
    host: string
    initialQuery: LocationQuery
    platform: PlatformType
    isNativeIos: boolean
    isNativeAndroid: boolean
    appVersion: string | undefined
    countryCode: string | undefined
    clid: string
    experiments: SelectedExperimentsData
    rankingParams: RankingParams
}

const DEFAULT_PLATFORM = 'desktop'
const IOS_NATIVE_PAYMENTS_MIN_VERSION = '1.1.0'
const ANDROID_NATIVE_PAYMENTS_MIN_VERSION = '1.0.35'

export const useAppStore = defineStore('app', () => {
    const route = useRoute()

    const bootstrapContext = ref<AppBootstrapContext>()

    const platform = computed(() => bootstrapContext.value?.platform || DEFAULT_PLATFORM)
    const host = computed(() => bootstrapContext.value?.host)
    const countryCode = computed(() => bootstrapContext.value?.countryCode)
    const rawRankingParams = computed(() => bootstrapContext.value?.rankingParams)
    const initialQuery = computed(() => bootstrapContext.value?.initialQuery || {})
    const initialClid = computed(() => bootstrapContext.value?.clid)
    const isNativeIos = computed(() => bootstrapContext.value?.isNativeIos || false)
    const isNativeAndroid = computed(() => bootstrapContext.value?.isNativeAndroid || false)
    const appVersion = computed(() => bootstrapContext.value?.appVersion)
    const experimentsRaw = computed(() => bootstrapContext.value?.experiments)
    const isGameDistribution = computed(() => !!initialQuery.value[SSR_QUERY.GAME_DISTRIBUTION])
    const isOppoWL = computed(() => isBestWebGamesHost(host.value))
    const isDebugMode = computed(() => isDebugQueryEnabled(initialQuery.value[SSR_QUERY.DEBUG]))
    const isNativeIosEnv = computed(() => isNativeIos.value)
    const isNativeAndroidEnv = computed(() => isNativeAndroid.value)
    const isNativeAppEnv = computed(
        () => isNativeIosEnv.value || isNativeAndroidEnv.value || Object.values(NATIVE_APP_CLIDS).includes(clid.value),
    )
    const isPWA = computed(() => Object.values(PWA_APP_CLIDS).includes(clid.value))
    const isAppEnv = computed(() => isNativeAppEnv.value || isPWA.value)
    const clientType = computed((): ClientType => {
        if (isNativeAppEnv.value) {
            return 'mobile-native'
        }
        if (platform.value !== 'desktop') {
            return 'mobile-web'
        }
        return 'desktop-web'
    })

    function isVersionAtLeast(minVersions: { ios?: string; android?: string }): boolean {
        if (!appVersion.value) {
            return false
        }
        if (isNativeIosEnv.value && minVersions.ios) {
            return compareVersionStrings(appVersion.value, minVersions.ios)
        }
        if (isNativeAndroidEnv.value && minVersions.android) {
            return compareVersionStrings(appVersion.value, minVersions.android)
        }
        return false
    }

    const isNativePaymentsSupported = computed(() =>
        isVersionAtLeast({ ios: IOS_NATIVE_PAYMENTS_MIN_VERSION, android: ANDROID_NATIVE_PAYMENTS_MIN_VERSION }),
    )
    const isAuthEnabled = computed(() => !isOppoWL.value)
    const isPurchaseEnabled = computed(() => {
        if (!isAuthEnabled.value) {
            return false
        }

        return isNativeAppEnv.value ? isNativePaymentsSupported.value : true
    })
    const isExportOnWidget = computed(() => initialQuery.value[SSR_QUERY.EXPORT_MODE] === 'widget')
    const isExportOnWhitelabel = computed(() => initialQuery.value[SSR_QUERY.EXPORT_MODE] === 'wl')
    const isPartnerEmbed = computed(
        () =>
            (route.name === ROUTE_NAMES.EXPORT_GAME_PAGE && initialQuery.value[SSR_QUERY.EXPORT_MODE] !== 'portal') ||
            route.name === ROUTE_NAMES.WIDGET_GAME_PAGE ||
            isOppoWL.value,
    )
    const isPortalEnv = computed(() => !isPartnerEmbed.value)
    const clid = computed(() => {
        if (TEST_GAMES.includes(route.params.game as string)) {
            return DEFAULT_CLIDS.directTest
        }

        return initialClid.value || UNKNOWN_CLID
    })

    const contentScope = computed(() => {
        // If isAppEnv is true, always return 'curated'
        if (isAppEnv.value) {
            if (isNativeIosEnv.value) {
                // temporary solution for limiting ios app content
                // no in-app, age 16+
                return 'curated-app'
            }
            return 'curated'
        }
        return undefined
    })

    const rankingParams = computed(() => {
        // AUD3 audience is only used for ranking behind the use_aud3 experiment
        if (experiments.value?.use_aud3?.sample !== 'group_1') {
            return { ...rawRankingParams.value, audience: undefined }
        }
        return rawRankingParams.value
    })

    const experiments = computed(() => {
        const isAudienceRankedAUD3 = rawRankingParams.value?.audience === 'AUD3'
        return {
            use_aud3: isAudienceRankedAUD3 ? experimentsRaw.value?.['use_aud3'] : undefined,
            'suggested-game-exp': experimentsRaw.value?.['suggested-game-exp'],
        }
    })

    function initialize(context: AppBootstrapContext) {
        bootstrapContext.value = context
    }

    /* MISC */

    function getWhiteLabelClid(partner: string) {
        return WHITE_LABEL_CLIDS[partner] || UNKNOWN_CLID
    }

    const pageStatus = ref<PageStatus>(200)
    const targetURL = ref<string>('')
    const headers = ref<Record<string, string>>({})

    function setHeaders(value: Record<string, string>) {
        headers.value = value
    }
    function setPageStatus(status: PageStatus) {
        pageStatus.value = status
    }
    function setTargetURL(target: string) {
        targetURL.value = target
    }

    // TODO remove this, and targetURL, setTargetURL, setPageStatus all together
    // use router methods instead
    const router = useRouter()
    watch(
        () => [pageStatus.value, targetURL.value],
        () => {
            if (targetURL.value !== '' && pageStatus.value > 300 && pageStatus.value < 400) {
                router.replace(targetURL.value)
                pageStatus.value = 200
                targetURL.value = ''
            }
        },
    )

    return {
        initialize,
        bootstrapContext,
        headers,
        pageStatus,
        targetURL,
        contentScope,
        clid,
        initialClid,
        host,
        initialQuery,
        platform,
        isGameDistribution,
        isDebugMode,
        isExportOnWidget,
        isExportOnWhitelabel,
        isPartnerEmbed,
        isPortalEnv,
        rawRankingParams,
        rankingParams,
        countryCode,
        experimentsRaw,
        experiments,
        isPWA,
        isNativeAppEnv,
        isNativeAndroidEnv,
        isNativeIosEnv,
        isNativeIos,
        isNativeAndroid,
        appVersion,
        isAppEnv,
        isAuthEnabled,
        isPurchaseEnabled,
        isVersionAtLeast,
        clientType,
        isOppoWL,

        /* MISC */
        setPageStatus,
        setHeaders,
        getWhiteLabelClid,
        setTargetURL,
    }
})
